import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useVoiceDictation — enregistrement vocal navigateur + transcription Whisper.
 *
 * Le micro est capté via `navigator.mediaDevices.getUserMedia` et `MediaRecorder`
 * (formats adaptés au navigateur : WebM/Opus, MP4/M4A, OGG). L'audio est envoyé
 * à l'edge function sécurisée `transcribe-audio`, qui appelle le serveur Whisper
 * côté backend (la clé API ne touche jamais le navigateur).
 *
 * Aucune donnée audio n'est conservée : le blob est libéré après transcription.
 */

export type VoiceStatus = 'idle' | 'requesting' | 'recording' | 'transcribing' | 'error';

/** Durée maximale d'un enregistrement (sécurité VPS). */
export const VOICE_MAX_SECONDS = 30;

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }
  for (const m of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* ignore */
    }
  }
  return undefined;
}

function extensionFor(type: string): string {
  if (type.includes('mp4')) return 'm4a';
  if (type.includes('ogg')) return 'ogg';
  return 'webm';
}

// ── Micro partagé (autorisation demandée UNE seule fois) ─────────────
// Le flux audio est conservé pour la session : chaque dictée le réutilise,
// donc le navigateur ne redemande jamais l'autorisation et l'utilisateur
// n'a jamais à « tester » le micro. Le flux est libéré à la fermeture de
// la page (pagehide) pour ne pas laisser le micro actif inutilement.
let sharedStream: MediaStream | null = null;
let unloadBound = false;

/** Libère le micro partagé (appelé à la fermeture de la page). */
export function stopSharedMicrophone(): void {
  if (sharedStream) {
    sharedStream.getTracks().forEach((t) => t.stop());
    sharedStream = null;
  }
}

function bindUnload(): void {
  if (unloadBound || typeof window === 'undefined') return;
  unloadBound = true;
  window.addEventListener('pagehide', stopSharedMicrophone);
}

async function acquireStream(): Promise<MediaStream> {
  if (sharedStream && sharedStream.getAudioTracks().some((t) => t.readyState === 'live')) {
    return sharedStream;
  }
  sharedStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  return sharedStream;
}

/**
 * Résultat d'une dictée : le texte transcrit ET la note audio elle-même.
 * Permet au chat IA d'envoyer le texte à l'IA tout en affichant l'audio.
 */
export interface VoiceResult {
  text: string;
  /** URL de lecture immédiate (blob local) de la note enregistrée. */
  audioUrl: string;
  /**
   * Blob de l'enregistrement. Le chat IA le téléverse dans le stockage du
   * compte pour que la note reste réécoutable après un rafraîchissement.
   */
  audioBlob: Blob;
  /** Durée de l'enregistrement en secondes. */
  durationSeconds: number;
  mimeType: string;
}

interface UseVoiceDictationParams {
  userId?: number;
  /** Langue forcée ('auto' = détection automatique). */
  language: string;
  /** Appelé avec le texte transcrit (champs « classiques » : insertion). */
  onTranscript?: (text: string) => void;
  /** Appelé quand la dictée doit aussi produire la note audio (chat IA). */
  onResult?: (result: VoiceResult) => void;
}

export function useVoiceDictation({ userId, language, onTranscript, onResult }: UseVoiceDictationParams) {
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);
  const onTranscriptRef = useRef(onTranscript);
  const onResultRef = useRef(onResult);
  const languageRef = useRef(language);
  const recordStartRef = useRef(0);

  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { languageRef.current = language; }, [language]);

  const supported = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined';

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    // On NE coupe PAS les pistes audio : le flux partagé est réutilisé pour
    // les dictées suivantes (autorisation micro demandée une seule fois).
    streamRef.current = null;
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    setSeconds(0);
  }, []);

  const transcribe = useCallback(async (blob: Blob, durationSeconds: number) => {
    if (!userId) {
      setError('Vous devez être connecté pour utiliser la dictée vocale.');
      setStatus('error');
      return;
    }
    setStatus('transcribing');
    try {
      const fd = new FormData();
      const type = blob.type || 'audio/webm';
      fd.append('file', blob, `audio.${extensionFor(type)}`);
      fd.append('userId', String(userId));
      fd.append('language', languageRef.current || 'auto');

      const base = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const anon = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
      const resp = await fetch(`${base}/functions/v1/transcribe-audio`, {
        method: 'POST',
        headers: { apikey: anon, Authorization: `Bearer ${anon}` },
        body: fd,
      });
      let data: Record<string, unknown> = {};
      try { data = (await resp.json()) as Record<string, unknown>; } catch { data = {}; }

      if (!resp.ok) {
        setError(String(data?.message || 'La transcription a échoué. Réessayez.'));
        setStatus('error');
        return;
      }
      const text = String(data?.text || '').trim();
      if (!text) {
        setError('La transcription est vide. Rapprochez-vous du micro et réessayez.');
        setStatus('error');
        return;
      }
      // Mode « chat IA » : on renvoie AUSSI la note audio, pour que le message
      // s'affiche comme un vocal (réécoutable) pendant que le TEXTE part à l'IA.
      if (onResultRef.current) {
        onResultRef.current({
          text,
          audioUrl: URL.createObjectURL(blob),
          audioBlob: blob,
          durationSeconds: Math.max(1, Math.round(durationSeconds)),
          mimeType: type,
        });
      } else {
        onTranscriptRef.current?.(text);
      }
      setStatus('idle');
      setSeconds(0);
      setError(null);
    } catch {
      setError('Connexion interrompue. Vérifiez votre réseau puis réessayez.');
      setStatus('error');
    }
  }, [userId]);

  const stop = useCallback(() => {
    clearTimer();
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try { rec.stop(); } catch { /* ignore */ }
    }
  }, [clearTimer]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    clearTimer();
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try { rec.stop(); } catch { /* ignore */ }
    }
    releaseStream();
    setStatus('idle');
    setSeconds(0);
    setError(null);
  }, [clearTimer, releaseStream]);

  const start = useCallback(async () => {
    if (status === 'recording' || status === 'requesting' || status === 'transcribing') return;
    setError(null);

    if (!supported) {
      setError("L'enregistrement vocal n'est pas pris en charge par ce navigateur.");
      setStatus('error');
      return;
    }

    setStatus('requesting');
    try {
      const stream = await acquireStream();
      streamRef.current = stream;
      cancelledRef.current = false;
      chunksRef.current = [];

      const mime = pickMimeType();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;

      rec.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        clearTimer();
        const type = rec.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        releaseStream();
        recorderRef.current = null;
        if (cancelledRef.current) {
          cancelledRef.current = false;
          setStatus('idle');
          setSeconds(0);
          return;
        }
        if (blob.size === 0) {
          setError('Aucun son enregistré. Réessayez.');
          setStatus('error');
          return;
        }
        const durationSeconds = recordStartRef.current
          ? (Date.now() - recordStartRef.current) / 1000
          : 0;
        void transcribe(blob, durationSeconds);
      };

      rec.start();
      recordStartRef.current = Date.now();
      setSeconds(0);
      setStatus('recording');
      timerRef.current = window.setInterval(() => {
        setSeconds((s) => {
          const next = s + 1;
          if (next >= VOICE_MAX_SECONDS) {
            stop();
            return VOICE_MAX_SECONDS;
          }
          return next;
        });
      }, 1000);
    } catch (e) {
      releaseStream();
      const name = (e as { name?: string })?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setError("Micro refusé. Autorisez l'accès au microphone dans votre navigateur.");
      } else if (name === 'NotFoundError') {
        setError('Aucun microphone détecté sur cet appareil.');
      } else {
        setError("Impossible de démarrer l'enregistrement vocal.");
      }
      setStatus('error');
    }
  }, [status, supported, stop, clearTimer, releaseStream, transcribe]);

  useEffect(() => { bindUnload(); }, []);

  useEffect(() => () => { clearTimer(); releaseStream(); }, [clearTimer, releaseStream]);

  return { supported, status, seconds, error, start, stop, cancel, reset };
}